import React, { useEffect, useRef, useState } from 'react';
import { getDeviceConfigApi } from '../../api/configurationApi';
import { rebootDeviceApi, turnOnDeviceApi, turnOffDeviceApi, getDeviceHistoryApi } from '../../api/shellyApi';
import { AppLabels } from '../../constant/constant';
import { renderTextWithTooltip, renderCriticalDeviceListWithDualSupply } from '../../utils/TextTooltipUtils.jsx';
import DeviceHistoryPanel from './DeviceHistoryPanel.jsx';

// Material UI imports
import {
  Typography,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Box,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  PowerSettingsNew as PowerOnIcon,
  RestartAlt as RestartIcon,
  History as HistoryIcon
} from '@mui/icons-material';

import { useAuth } from '../../context/AuthContext';

// DeviceTrouble styled components
import {
  DeviceTroubleContainer,
  DeviceTroubleOuterContainer,
  DeviceTroublePageHeader,
  DeviceTroublePanel,
  DeviceTroubleTableContainer,
  DeviceTroublePrimaryButton,
  DeviceTroubleSecondaryButton,
  DeviceTroubleLoadingMessage,
  DeviceTroubleErrorMessage,
  DeviceTroubleSearchPanel,
  DeviceTroubleSearchField,
  DeviceTroubleLayout,
  DeviceTroubleSearchResults,
  DeviceTroubleConsoleArea,
  DeviceTroubleConsolePanel,
  DeviceTroubleConsoleBody,
  DeviceTroubleConsolePre,
  DeviceTroubleActionIcons,
  DeviceTroubleActionIconButton,
  DeviceTroubleHistoryIconButton,
} from './DeviceTrouble.styles';
import { DashboardMonospaceText } from '../dashBoard/Dashboard.styles';
import StatusIndicator from '../../components/StatusIndicator.jsx';

const initialLogsState = [
  AppLabels.deviceTroubleLabels.awaitingCommand
]
export default function DeviceTrouble() {
  const [deviceMac, setDeviceMac] = useState('');         // search by device MAC
  // const [loading, setLoading] = useState(false);          // initial page loading state - removed as not needed
  const [searchLoading, setSearchLoading] = useState(false); // search operation loading state
  const [error, setError] = useState(null);               // error state
  const [deviceData, setDeviceData] = useState(null);     // device details from API
  const [searched, setSearched] = useState(false);        // to show empty state text only after search
  const [actionStates, setActionStates] = useState({
    restarting: false,
    turningOn: false,
    turningOff: false
  }); // Individual action states for each button

  const [restartDisabled, setRestartDisabled] = useState(false); // Separate state for 15-second disable period

  // History panel state
  const [showHistory, setShowHistory] = useState(false);  // history panel visibility
  const [historyData, setHistoryData] = useState([]);     // device history data
  const [historyLoading, setHistoryLoading] = useState(false); // history loading state
  const [historyError, setHistoryError] = useState(null); // history error state

  // Console state
  const [logs, setLogs] = useState(initialLogsState);
  const consoleEndRef = useRef(null);
  const { user } = useAuth(); // Current authenticated user
  const email = user?.email || '';

  useEffect(() => {
    consoleEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  // Clear search results when device MAC input is cleared
  useEffect(() => {
    if (!deviceMac.trim()) {
      setDeviceData(null);
      setSearched(false);
      setError(null);
      setShowHistory(false);
      setHistoryData([]);
      setHistoryError(null);
    }
  }, [deviceMac]);

  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to the top on component mount
    window.history.scrollRestoration = 'manual'; // NOSONAR
  }, []);

  // Validate MAC address format (basic validation) - commented out for now
  // const isValidMacAddress = (mac) => {
  //   const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$|^([0-9A-Fa-f]{12})$/;
  //   return macRegex.test(mac.trim());
  // };

  const onSearch = async (e) => {
    e.preventDefault();
    const trimmedMac = deviceMac.trim();

    if (!trimmedMac) {
      setError(AppLabels.deviceTroubleLabels.invalidMacAddress);
      return;
    }


    setSearchLoading(true);
    setError(null);
    setDeviceData(null);
    setSearched(true);
    setLogs(initialLogsState);
    try {
      const response = await getDeviceConfigApi(trimmedMac);
      setDeviceData(response);
    } catch (err) {
      console.error('Error fetching device:', err);
      setError(err.response?.data?.message || AppLabels.deviceTroubleLabels.deviceNotFound);
    } finally {
      setSearchLoading(false);
    }
  };

  // Device restart workflow using Shelly API
  const pingDevice = async (device) => {
    if (actionStates.restarting || restartDisabled) return;
    setActionStates(prev => ({ ...prev, restarting: true }));

    setLogs(prev => [
      ...prev,
      `> Restart ${device.device_mac} - (${device.sp_DeviceName})`,
      '• Initiating device restart…',
    ]);

    try {
      // Call the Shelly API to restart the device
      const payload = createDevicePayload(device);
      const result = await rebootDeviceApi(payload);

      setLogs(prev => [
        ...prev,
        '• Device restart command sent successfully',
        `• Status: ${result.status || 'success'}`,
        `• Response: ${result.message || 'Device reboot initiated'}`,
        '• Device is restarting...',
        '--- RESTART INITIATED SUCCESSFULLY ---',
        '———'
      ]);

    } catch (error) {
      setLogs(prev => [
        ...prev,
        '• Device restart failed',
        `• Error: ${error.response?.data?.message || error.message}`,
        '--- RESTART FAILED ---',
        '———'
      ]);

    } finally {
      // Stop loading spinner immediately after API response
      setActionStates(prev => ({ ...prev, restarting: false }));

      // Keep button disabled for 15 seconds after restart command
      setRestartDisabled(true);
      setTimeout(() => {
        setRestartDisabled(false);
      }, 20000); // 20 seconds delay
    }
  };

  const clearLogs = () => setLogs([AppLabels.deviceTroubleLabels.awaitingCommand]); // Commented out as console is disabled

  // Common method to create device action payload
  const createDevicePayload = (device) => ({
    deviceMac: device.device_mac,
    triggeredBy: email
  });

  // Device turn on workflow
  const turnOnDevice = async (device) => {
    if (actionStates.turningOn) return;
    setActionStates(prev => ({ ...prev, turningOn: true }));

    setLogs(prev => [
      ...prev,
      `> Turn on ${device.device_mac} - (${device.sp_DeviceName})`,
      '• Initiating device turn on…',
    ]);

    try {
      const payload = createDevicePayload(device);
      const result = await turnOnDeviceApi(payload);

      setLogs(prev => [
        ...prev,
        '• Device turn on command sent successfully',
        `• Status: ${result.status || 'success'}`,
        `• Response: ${result.message || 'Device turned on'}`,
        '--- DEVICE TURNED ON ---',
        '———'
      ]);

    } catch (error) {
      setLogs(prev => [
        ...prev,
        '• Device turn on failed',
        `• Error: ${error.response?.data?.message || error.message}`,
        '--- TURN ON FAILED ---',
        '———'
      ]);

    } finally {
      setActionStates(prev => ({ ...prev, turningOn: false }));
    }
  };

  // Device turn off workflow
  const turnOffDevice = async (device) => {
    if (actionStates.turningOff) return;
    setActionStates(prev => ({ ...prev, turningOff: true }));

    setLogs(prev => [
      ...prev,
      `> Turn off ${device.device_mac} - (${device.sp_DeviceName})`,
      '• Initiating device turn off…',
    ]);

    try {
      const payload = createDevicePayload(device);
      const result = await turnOffDeviceApi(payload);

      setLogs(prev => [
        ...prev,
        '• Device turn off command sent successfully',
        `• Status: ${result.status || 'success'}`,
        `• Response: ${result.message || 'Device turned off'}`,
        '--- DEVICE TURNED OFF ---',
        '———'
      ])
    } catch (error) {
      setLogs(prev => [
        ...prev,
        '• Device turn off failed',
        `• Error: ${error.response?.data?.message || error.message}`,
        '--- TURN OFF FAILED ---',
        '———'
      ]);

    } finally {
      setActionStates(prev => ({ ...prev, turningOff: false }));
    }
  };

  // Load device history
  const loadDeviceHistory = async (device) => {
    if (historyLoading) return;

    setHistoryLoading(true);
    setHistoryError(null);
    setShowHistory(true);

    try {
      const result = await getDeviceHistoryApi(device.device_mac);
      setHistoryData(result ?? []);
    } catch (error) {
      console.error('Error loading device history:', error);
      setHistoryError(error.response?.data?.message || error.message || 'Failed to load device history');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Close history panel
  const closeHistoryPanel = () => {
    setShowHistory(false);
    setHistoryData([]);
    setHistoryError(null);
  };

  // Format device data for display
  const formatDeviceForDisplay = (device) => {
    if (!device) return null;
    
    // Create dual supply devices data (similar to Dashboard pattern)
    const dualSupplyDevices = device.dualcriticaldevices;
    
    // Extract critical devices list (combine critical and dual supply devices)  
    const allCriticalDevices = [...(device.criticaldevices || []), ...(dualSupplyDevices || [])]
    const criticalDevicesList = allCriticalDevices.map(cd => cd.critical_id || cd.dual_critical_id).filter(id => id !== null && id !== undefined).join(', ') || AppLabels.notApplicable;
      

    return {
      device_mac: device.device_mac ?? AppLabels.notApplicable,
      sp_DeviceName: device.sp_DeviceName?.toUpperCase() ?? AppLabels.notApplicable,
      store_name: device.store_name ?? AppLabels.notApplicable,
      location_name: device.location_name ?? AppLabels.notApplicable,
      status: device.status.toLowerCase() === AppLabels.outageLabels.online ? AppLabels.outageLabels.online : AppLabels.outageLabels.offline || AppLabels.notApplicable,
      model: device.model ?? AppLabels.notApplicable,
      criticalDeviceList: criticalDevicesList,
      criticalDevices: device.criticaldevices || [],
      dualSupplyDevices: dualSupplyDevices || [],
    };
  };

  const displayDevice = formatDeviceForDisplay(deviceData);
  const hasResults = displayDevice && searched;

  // Initial loading state removed - not needed for this component

  return (
    <DeviceTroubleContainer>
      <DeviceTroubleOuterContainer>
        <DeviceTroublePageHeader>
          <Typography variant="h2" component="h2">
            {AppLabels.deviceTroubleLabels.pageTitle}
          </Typography>
          <Typography className="subtitle" component="p">
            {AppLabels.deviceTroubleLabels.pageDescription}
          </Typography>
        </DeviceTroublePageHeader>

        {/* Search bar */}
        <DeviceTroubleSearchPanel component="form" onSubmit={onSearch}>
          <DeviceTroubleSearchField
           label="Device MAC ID"
            placeholder="Enter Device MAC ID (e.g., shellyplusplugs-c82e180a3e40)"
           value={deviceMac}
            onChange={e => setDeviceMac(e.target.value)}
            variant="outlined"
            size="small"
          />
          <DeviceTroublePrimaryButton
            type="submit"
            startIcon={<SearchIcon />}
            disabled={searchLoading || !deviceMac.trim()}
          >
            {searchLoading ? AppLabels.deviceTroubleLabels.searching : AppLabels.deviceTroubleLabels.searchButton}
          </DeviceTroublePrimaryButton>
        </DeviceTroubleSearchPanel>

        {/* Search Loading State */}
        {searchLoading && (
          <DeviceTroublePanel>
            <DeviceTroubleLoadingMessage>
              <CircularProgress size={20} sx={{ marginRight: '12px', color: '#4f7cff' }} />
              {AppLabels.deviceTroubleLabels.searchingDevice}
            </DeviceTroubleLoadingMessage>
          </DeviceTroublePanel>
        )}

        {/* Error state */}
        {error && searched && !searchLoading && !hasResults && (
          <DeviceTroublePanel>
            <Box sx={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '12px',
              padding: '24px',
              textAlign: 'center'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: '24px', marginRight: '12px' }}>⚠️</Typography>
                <Typography sx={{
                  color: '#ef4444',
                  fontSize: '1.1rem',
                  fontWeight: '600'
                }}>
                  {AppLabels.deviceTroubleLabels.errorLoadingDevice}
                </Typography>
              </Box>
              <DeviceTroubleErrorMessage sx={{
                color: '#ef4444',
                fontSize: '1rem',
                textAlign: 'center'
              }}>
                {error}
              </DeviceTroubleErrorMessage>
              <Typography sx={{
                color: '#666',
                fontSize: '0.9rem'
              }}>
                {AppLabels.deviceTroubleLabels.tryDifferentSearch}
              </Typography>
            </Box>
          </DeviceTroublePanel>
        )}

        {/* Two-column layout: results & console */}
        {hasResults && (
          <DeviceTroubleLayout>
            {/* Left: Search Results */}
            <DeviceTroubleSearchResults>
              <DeviceTroublePanel>
                <Typography className="subtitle" component="p" sx={{ fontSize: '1rem', margin: 0, color: 'black !important' }}>
                  {AppLabels.deviceTroubleLabels.searchResults}
                </Typography>
                <DeviceTroubleTableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{AppLabels.deviceTroubleLabels.deviceNameHeader}</TableCell>
                        <TableCell>{AppLabels.deviceTroubleLabels.deviceMacHeader}</TableCell>
                        <TableCell>{AppLabels.deviceTroubleLabels.siteHeader}</TableCell>
                        <TableCell>{AppLabels.deviceTroubleLabels.locationHeader}</TableCell>
                        <TableCell>{AppLabels.deviceTroubleLabels.statusHeader}</TableCell>
                        <TableCell> {AppLabels.deviceTroubleLabels.criticalDevices} </TableCell>
                        <TableCell> {AppLabels.deviceTroubleLabels.actionItems} </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>{displayDevice.sp_DeviceName}</TableCell>
                        <TableCell>
                          <DashboardMonospaceText>{displayDevice.device_mac}</DashboardMonospaceText>
                        </TableCell>
                        <TableCell>{displayDevice.store_name}</TableCell>
                        <TableCell>
                          {renderTextWithTooltip(displayDevice.location_name, 'locationId')}</TableCell>
                        <TableCell>
                          {/* <DeviceTroubleStatusChip
                            label={displayDevice.status}
                            size="small"
                          /> */}
                          <StatusIndicator status={displayDevice.status} size="medium" />
                        </TableCell>
                        <TableCell>
                          {renderCriticalDeviceListWithDualSupply(
                            displayDevice.criticalDeviceList,
                            displayDevice.criticalDevices,
                            displayDevice.dualSupplyDevices
                          )}
                        </TableCell>
                        <TableCell>
                          <DeviceTroubleActionIcons>
                            <Tooltip title={AppLabels.deviceTroubleLabels.turnOnButton} arrow>
                              <DeviceTroubleActionIconButton
                                actiontype="on"
                                size="small"
                                disabled={actionStates.turningOn}
                                onClick={() => turnOnDevice(displayDevice)}
                                startIcon={actionStates.turningOn ? <CircularProgress size={16} sx={{ color: '#ffffff' }} /> : <PowerOnIcon />}
                              />
                            </Tooltip>
                            <Tooltip title={AppLabels.deviceTroubleLabels.turnOffButton} arrow>
                              <DeviceTroubleActionIconButton
                                actiontype="off"
                                size="small"
                                disabled={actionStates.turningOff}
                                onClick={() => turnOffDevice(displayDevice)}
                                startIcon={actionStates.turningOff ? <CircularProgress size={16} sx={{ color: '#ffffff' }} /> : <PowerOnIcon sx={{ color: '#ffffff' }} />}
                              />
                            </Tooltip>
                            <Tooltip title={AppLabels.deviceTroubleLabels.restartButton} arrow>
                              <DeviceTroubleActionIconButton
                                actiontype="restart"
                                size="small"
                                disabled={actionStates.restarting || restartDisabled}
                                onClick={() => pingDevice(displayDevice)}
                                startIcon={actionStates.restarting ? <CircularProgress size={16} sx={{ color: '#ffffff' }} /> : <RestartIcon />}
                              />
                            </Tooltip>
                            <Tooltip title={AppLabels.deviceTroubleLabels.historyButton} arrow>
                              <DeviceTroubleHistoryIconButton
                                size="small"
                                disabled={historyLoading}
                                onClick={() => loadDeviceHistory(displayDevice)}
                                startIcon={<HistoryIcon />}
                              />
                            </Tooltip>
                          </DeviceTroubleActionIcons>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  {/* Device History Panel - Shows below table when history icon is clicked */}
                  {hasResults && showHistory && (
                    <DeviceHistoryPanel
                      historyData={historyData}
                      loading={historyLoading}
                      error={historyError}
                      onClose={closeHistoryPanel}
                      onRefresh={() => loadDeviceHistory(displayDevice)}
                      deviceMac={displayDevice.device_mac}
                    />
                  )}
                </DeviceTroubleTableContainer>
              </DeviceTroublePanel>
            </DeviceTroubleSearchResults>

            {/* Right: Diagnostic Console */}
            <DeviceTroubleConsoleArea>
              <DeviceTroubleConsolePanel>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '12px'
                }}>
                  <Typography sx={{
                    fontSize: '13px',
                    fontWeight: 500,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase'
                  }}>
                    {AppLabels.deviceTroubleLabels.consoleTitle}
                  </Typography>
                  <DeviceTroubleSecondaryButton
                    onClick={clearLogs}
                    size="small"
                    disabled={logs.length < 2}
                  >
                    {AppLabels.deviceTroubleLabels.clearLogs}
                  </DeviceTroubleSecondaryButton>
                </Box>

                <DeviceTroubleConsoleBody>
                  <DeviceTroubleConsolePre>
                    {logs.map((l, i) => <div key={`log-entry-${i}-${l.slice(0, 20)}`}>{l}</div>)}
                    <span ref={consoleEndRef} />
                  </DeviceTroubleConsolePre>
                </DeviceTroubleConsoleBody>

              </DeviceTroubleConsolePanel>
            </DeviceTroubleConsoleArea>
          </DeviceTroubleLayout>
        )}
      </DeviceTroubleOuterContainer>
    </DeviceTroubleContainer>
  );
}
